import CheckIcon from '@mui/icons-material/CheckOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { ReactElement } from 'react'
import { useMemo } from 'react'

import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'

/**
 * Hook to calculate tab icons for bid offer price tabs based on bid validation state.
 * Returns icons indicating bid status: valid (green check), invalid (red error), or pending (undefined).
 *
 * @param bidsPerActivationGroup - Map of aggregated bid wrappers by activation group UUID
 * @returns Map of tab icons by activation group UUID
 *
 * @example
 * ```tsx
 * const tabIcons = useBidOfferPriceTabIcons(bidsPerActivationGroup)
 * const icon = tabIcons.get(activationGroupUuid)
 * ```
 */
export function useBidOfferPriceTabIcons(
  bidsPerActivationGroup: Map<string, AggregatedBidWrapper>,
): Map<string, ReactElement | undefined> {
  return useMemo(() => {
    const icons = new Map<string, ReactElement | undefined>()
    for (const bid of bidsPerActivationGroup.values()) {
      icons.set(bid.activationGroup.uuid, getTabStateIcon(bid.state))
    }
    return icons
  }, [bidsPerActivationGroup])
}

function getTabStateIcon(state: 'valid' | 'invalid' | 'pending'): ReactElement | undefined {
  if (!state) return undefined
  switch (state) {
    case 'valid':
      return <CheckIcon sx={{ color: 'green' }} />
    case 'invalid':
      return <ErrorOutlineIcon sx={{ color: 'red' }} />
    case 'pending':
      return undefined
  }
}
